import type { Geometry, Position } from 'geojson'
import { REGION_LABEL_FREE_ROOM } from '../../lib/map_config'

/**
 * Where an appellation's name goes, and how much room there is for it there.
 *
 * Arithmetic on coordinates and nothing else: no map, no DOM, no import of
 * maplibre-gl even for a type. region_layer.ts decides WHEN each of these is
 * run — the plain anchor as a feature arrives, the child-free one later, off
 * the critical path — and this file is what they cost.
 */

type Rings = Position[][]

export interface Box {
  west: number
  south: number
  east: number
  north: number
}

function boundsOf(ring: Position[]): Box {
  let west = Infinity
  let south = Infinity
  let east = -Infinity
  let north = -Infinity

  for (const [lng, lat] of ring) {
    if (lng < west) west = lng
    if (lng > east) east = lng
    if (lat < south) south = lat
    if (lat > north) north = lat
  }

  return { west, south, east, north }
}

// The part of a geometry a name belongs on. An appellation in several pieces is
// named on its largest, by bounding box — the same cheap proxy for area the
// server's `largest_part_with_box` uses for the camera.
function largestPart(geometry: Geometry): Rings | null {
  if (geometry.type === 'Polygon') return geometry.coordinates
  if (geometry.type !== 'MultiPolygon') return null

  let largest: Rings | null = null
  let largestArea = -1

  for (const polygon of geometry.coordinates) {
    const box = boundsOf(polygon[0] ?? [])
    const area = (box.east - box.west) * (box.north - box.south)
    if (area <= largestArea) continue

    largest = polygon
    largestArea = area
  }

  return largest
}

// How far a point is from the nearest edge of a polygon: positive inside,
// negative outside. One pass does both jobs — the even-odd ray cast and the
// nearest-segment distance read the same edges. `lngScale` shrinks longitude to
// the length it has at this latitude, so "far from the edge" means far on the
// ground rather than far in degrees, and the answer is in degrees of latitude.
function insideDistance(lng: number, lat: number, rings: Rings, lngScale: number): number {
  let inside = false
  let nearest = Infinity

  for (const ring of rings) {
    for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index++) {
      const from = ring[index]
      const to = ring[previous]
      const fromLng = from[0]
      const fromLat = from[1]
      const toLng = to[0]
      const toLat = to[1]

      if (fromLat > lat !== toLat > lat && lng < ((toLng - fromLng) * (lat - fromLat)) / (toLat - fromLat) + fromLng) {
        inside = !inside
      }

      let edgeLng = fromLng
      let edgeLat = fromLat
      const spanLng = (toLng - fromLng) * lngScale
      const spanLat = toLat - fromLat

      if (spanLng !== 0 || spanLat !== 0) {
        const along =
          ((lng - fromLng) * lngScale * spanLng + (lat - fromLat) * spanLat) / (spanLng * spanLng + spanLat * spanLat)

        if (along > 1) {
          edgeLng = toLng
          edgeLat = toLat
        } else if (along > 0) {
          edgeLng += (toLng - fromLng) * along
          edgeLat += spanLat * along
        }
      }

      const offLng = (lng - edgeLng) * lngScale
      const offLat = lat - edgeLat
      nearest = Math.min(nearest, offLng * offLng + offLat * offLat)
    }
  }

  return (inside ? 1 : -1) * Math.sqrt(nearest)
}

// How far a point is from a box, in the same measure: nothing when it is in it.
// What lets the child-free search skip a child that is too far away to matter
// without walking its outline.
function boxDistance(lng: number, lat: number, box: Box, lngScale: number): number {
  const offLng = Math.max(box.west - lng, 0, lng - box.east) * lngScale
  const offLat = Math.max(box.south - lat, 0, lat - box.north)

  return Math.sqrt(offLng * offLng + offLat * offLat)
}

// How finely an anchor is found: to a hundredth of the shape's narrower side,
// and never finer than 0.002 degrees, which is about 200 metres. A name does
// not need its point to the metre; what it needs is a point comfortably inside
// and a `room` that is right to a few per cent, and a search that stops there
// opens a third of the cells of one that goes to 200 metres across a region
// three degrees wide.
const FINEST = 0.002
const RELATIVE_PRECISION = 0.01

// The most cells one search may open. A shape this never converges on is
// degenerate, and the best point found so far is still inside it.
const CELL_BUDGET = 2400

interface Cell {
  lng: number
  lat: number
  half: number
  distance: number
  // The most any point in the cell can be worth: its centre, plus the cell's
  // own reach.
  best: number
}

/**
 * The point of a box that is worth most by `worth`, to the precision above: the pole of
 * inaccessibility when `worth` is the distance to a polygon's edge.
 *
 * Cells are opened best-first and split in four, and a cell whose best possible
 * point cannot beat the one in hand is never opened. That bound needs only one
 * thing of `worth` — that it changes by no more than the distance moved — which
 * is true of a distance to an edge and stays true of the least of several, so
 * the same search finds a plain pole and a child-free one.
 *
 * It replaced a fixed 7 by 7 grid. The grid's point was comfortably inside and
 * that was all it had to be; now the DISTANCE at the point ranks the name
 * (`room`), and a grid's answer is short by up to a cell.
 */
function pole(box: Box, lngScale: number, worth: (lng: number, lat: number, floor: number) => number): Cell {
  const width = box.east - box.west
  const height = box.north - box.south
  const size = Math.min(width * lngScale, height)
  const precision = Math.max(FINEST, size * RELATIVE_PRECISION)

  let winner: Cell | null = null

  // `floor` is what a cell's centre has to beat for the cell to matter at all:
  // at or under it, not even the cell's best corner can match the point in
  // hand. A `worth` that can tell early it will come in at or under it may stop
  // there and return what it has — the cell is discarded either way, and a
  // figure that low can never be taken for the winner.
  const cell = (lng: number, lat: number, half: number): Cell => {
    const reach = half * Math.SQRT2
    const floor = winner ? winner.distance - reach : -Infinity
    const distance = worth(lng, lat, floor)

    return { lng, lat, half, distance, best: distance + reach }
  }

  winner = cell(box.west + width / 2, box.south + height / 2, 0)
  if (!(size > 0)) return winner

  // The first row of cells is square on the ground and covers the box.
  const half = size / 2
  const stepLng = size / lngScale
  const queue: Cell[] = []

  for (let lng = box.west; lng < box.east; lng += stepLng) {
    for (let lat = box.south; lat < box.north; lat += size) {
      queue.push(cell(lng + stepLng / 2, lat + half, half))
    }
  }

  let opened = queue.length

  while (queue.length > 0 && opened < CELL_BUDGET) {
    // A linear scan for the most promising cell. The queue is a few dozen
    // cells, and a heap would cost more to keep than this does to read.
    let top = 0
    for (let index = 1; index < queue.length; index++) {
      if (queue[index].best > queue[top].best) top = index
    }

    const [next] = queue.splice(top, 1)
    if (next.distance > winner.distance) winner = next
    if (next.best - winner.distance <= precision) continue

    const quarter = next.half / 2
    const quarterLng = quarter / lngScale

    for (const [signLng, signLat] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      const child = cell(next.lng + signLng * quarterLng, next.lat + signLat * quarter, quarter)
      opened++
      if (child.distance > winner.distance) winner = child
      if (child.best - winner.distance > precision) queue.push(child)
    }
  }

  return winner
}

/** An appellation's shape, as much of it as lettering needs. */
export interface LabelShape {
  rings: Rings
  box: Box
  lngScale: number
  /** The point of the shape furthest from its own edge. */
  at: Position
  /** How far that is, in degrees of latitude: the radius of the largest circle that fits. */
  room: number
}

/**
 * The point an appellation's name is drawn at when nothing nests inside it, and
 * the room round it: the point of its largest part that is furthest from the
 * boundary.
 *
 * NOT the representative point the server stores. That is the centre of the
 * bounding box, and for 28 of the 276 American appellations it falls outside
 * the polygon altogether — it put "Temecula Valley" on the pinch of the bowtie,
 * over the town, and knocked out the town's own label. A pole of
 * inaccessibility is always inside and as far from every edge as the shape
 * allows, which is where a cartographer would letter it.
 *
 * Null for a geometry with no area to letter. About a tenth of a millisecond
 * for an overview shape and one or two for the most detailed outline in the
 * atlas, once per appellation as it arrives.
 */
export function labelShape(geometry: Geometry): LabelShape | null {
  const rings = largestPart(geometry)
  if (!rings || !rings[0] || rings[0].length < 3) return null

  const box = boundsOf(rings[0])
  const lngScale = Math.cos((((box.south + box.north) / 2) * Math.PI) / 180)
  const centre: Position = [(box.west + box.east) / 2, (box.south + box.north) / 2]
  if (box.east === box.west || box.north === box.south) return { rings, box, lngScale, at: centre, room: 0 }

  const found = pole(box, lngScale, (lng, lat) => insideDistance(lng, lat, rings, lngScale))

  // A shape so thin that the search found nothing inside it. The box centre may
  // be outside it; a name slightly off a sliver is better than no name.
  if (!(found.distance > 0)) return { rings, box, lngScale, at: centre, room: 0 }

  return { rings, box, lngScale, at: [found.lng, found.lat], room: found.distance }
}

/** `labelShape`'s point alone, and a point geometry as it is. */
export function labelAnchor(geometry: Geometry): Position | null {
  if (geometry.type === 'Point') return geometry.coordinates

  return labelShape(geometry)?.at ?? null
}

/** Whether `child`'s own anchor lies inside `parent`: the test for "nests in it". */
export function nestsIn(child: LabelShape, parent: LabelShape): boolean {
  const [lng, lat] = child.at
  const { box } = parent
  if (lng <= box.west || lng >= box.east || lat <= box.south || lat >= box.north) return false

  return insideDistance(lng, lat, parent.rings, parent.lngScale) > 0
}

/** Whether a point is on the shape's ground (its largest part, holes excluded). */
export function shapeHolds(shape: LabelShape, lng: number, lat: number): boolean {
  const { box } = shape
  if (lng <= box.west || lng >= box.east || lat <= box.south || lat >= box.north) return false

  return insideDistance(lng, lat, shape.rings, shape.lngScale) > 0
}

/**
 * Where a PARENT's name goes: the point of the parent furthest from its own
 * edge AND from every child's, or null when it should stay where it is.
 *
 * Nothing is filled any more, so a name is the only thing inside a boundary
 * that says whose ground it is. "Napa Valley" set at the middle of Napa Valley
 * lands on Oakville and reads as Oakville's name; set in the largest part of
 * the valley that no sub-appellation covers, it reads as what it is.
 *
 * Null when the best such point has less than `REGION_LABEL_FREE_ROOM` round
 * it: a parent that is nearly all children has nowhere better, and its name
 * keeps the plain anchor.
 *
 * This is the expensive one — every candidate point is measured against the
 * parent's outline and against each child near enough to matter — which is why
 * region_layer.ts runs it off the critical path and keeps the answer. A child
 * further away than the best distance in hand is skipped by its box alone.
 */
export function childFreeAnchor(parent: LabelShape, children: readonly LabelShape[]): Position | null {
  if (children.length === 0) return null

  const { rings, box, lngScale } = parent
  const found = pole(box, lngScale, (lng, lat, floor) => {
    let distance = insideDistance(lng, lat, rings, lngScale)

    for (const child of children) {
      // Outside the parent, or already under what its cell would need to be
      // opened: the rest of the children can only take it lower.
      if (distance <= 0 || distance <= floor) break
      if (boxDistance(lng, lat, child.box, lngScale) >= distance) continue

      distance = Math.min(distance, -insideDistance(lng, lat, child.rings, lngScale))
    }

    return distance
  })

  return found.distance > REGION_LABEL_FREE_ROOM ? [found.lng, found.lat] : null
}
